import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useMutation, useQuery } from "@tanstack/react-query";
import {
  allergySummaryLine,
  codeStatusLabel,
  codeStatusShortLabel,
  codeStatusTone,
  errorWords,
} from "@alm/domain";
import { Download, FileText, Pencil, Printer, User } from "@/lib/icons";
import { toast } from "sonner";
import { EmptyState } from "@/components/app/lists/empty-state";
import { ReadFailed } from "@/components/app/lists/read-failed";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { Skeleton } from "@/components/ui/skeleton";
import { Spinner } from "@/components/ui/spinner";
import { printFile, saveFile } from "@/lib/files/download";
import { orpc, orpcClient } from "@/lib/orpc";
import { useChartActions } from "./-components/chart/chart-actions";
import { FaceSheetContacts } from "./-components/chart/face-sheet-contacts";
import { FaceSheetVitals } from "./-components/chart/face-sheet-vitals";
import { useZone } from "@/lib/facility/zone";
import { shown, withheldOf } from "@/lib/policy/withheld";
import { FaceSheetBlock } from "@/routes/_authenticated/_care/residents/$residentId/-components/chart/face-sheet-block";
import { FieldRow, formatSheetDate, formatSsn } from "./-components/chart/face-sheet-parts";

export const Route = createFileRoute("/_authenticated/_care/residents/$residentId/facesheet")({
  component: FaceSheetTab,
});

/** The chart's identity page, on screen; print and download render the filed PDF. */
function FaceSheetTab() {
  const zone = useZone();
  const { residentId } = Route.useParams();
  const navigate = useNavigate();
  const { editResident } = useChartActions();
  const sheet = useQuery(orpc.care.residents.faceSheetData.queryOptions({ input: { residentId } }));
  const download = useMutation(
    orpc.care.residents.faceSheet.mutationOptions({
      onSuccess: saveFile,
      onError: (error: Error) => toast.error("Download failed", { description: errorWords(error) }),
    }),
  );
  const print = useMutation({
    mutationFn: async () => {
      printFile(await orpcClient.care.residents.faceSheet({ residentId }));
    },
    onError: (error: Error) => toast.error("Print failed", { description: errorWords(error) }),
  });
  const goTo = (to: "/residents/$residentId/vitals" | "/residents/$residentId") =>
    void navigate({ to, params: { residentId } });

  if (sheet.isPending) {
    return (
      <Card>
        <CardContent className="flex flex-col gap-4 py-6">
          <Skeleton className="h-8 w-full" />
          <Skeleton className="h-32 w-full" />
          <Skeleton className="h-32 w-full" />
        </CardContent>
      </Card>
    );
  }
  // NOT_FOUND is the only answer that means there is no such record for this reader; a resident in
  // another building answers the same way. Anything else is a read that did not finish, and calling
  // it "not found" would send them to ask about access they already have.
  const refusedCode = (sheet.error as { code?: unknown } | null)?.code;
  if (!sheet.data && sheet.isError && refusedCode !== "NOT_FOUND") {
    return (
      <Card>
        <CardContent className="py-6">
          <ReadFailed
            message="Could not load the face sheet."
            error={sheet.error}
            retrying={sheet.isFetching}
            onRetry={() => void sheet.refetch()}
          />
        </CardContent>
      </Card>
    );
  }
  if (!sheet.data) {
    return (
      <Card className="py-0">
        <EmptyState
          title="Resident not found"
          description="This record may have been moved or you may not have access to it."
        />
      </Card>
    );
  }

  const { resident, facilityName, primaryProvider, primaryPharmacy } = sheet.data;
  const dnr = codeStatusTone(resident.codeStatus) === "crit";
  const hidden = withheldOf(resident);
  const provider = primaryProvider
    ? [
        `${primaryProvider.firstName} ${primaryProvider.lastName}`.trim() +
          (primaryProvider.credentials ? `, ${primaryProvider.credentials}` : ""),
        primaryProvider.specialty,
        primaryProvider.phone,
      ]
        .filter(Boolean)
        .join(", ")
    : resident.attendingPhysician;
  const pharmacy = primaryPharmacy
    ? [
        primaryPharmacy.name,
        primaryPharmacy.phone,
        [
          primaryPharmacy.address,
          primaryPharmacy.city,
          primaryPharmacy.state,
          primaryPharmacy.zipCode,
        ]
          .filter(Boolean)
          .join(", ") || null,
      ]
        .filter(Boolean)
        .join(", ")
    : resident.pharmacy;

  return (
    <Card>
      <CardHeader className="flex flex-row flex-wrap items-center justify-between gap-3 space-y-0">
        <div>
          <CardTitle className="flex items-center gap-2">
            <FileText className="text-muted-foreground size-4" aria-hidden="true" />
            Face sheet
          </CardTitle>
          <CardDescription>{facilityName || "Assisted living facility"}</CardDescription>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" disabled={print.isPending} onClick={() => print.mutate()}>
            {print.isPending ? <Spinner /> : <Printer className="size-4" />}
            {print.isPending ? "Printing…" : "Print"}
          </Button>
          <Button
            variant="outline"
            disabled={download.isPending}
            onClick={() => download.mutate({ residentId })}
          >
            {download.isPending ? <Spinner /> : <Download className="size-4" />}
            {download.isPending ? "Downloading…" : "Download PDF"}
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        <div className="mb-5 flex items-center gap-4 border-b pb-4">
          <span className="relative shrink-0">
            {resident.photoUrl ? (
              <img
                src={resident.photoUrl}
                alt={`${resident.firstName} ${resident.lastName}`}
                className={`size-20 rounded-md border object-cover ${dnr ? "border-destructive ring-destructive ring-2 ring-offset-2" : ""}`}
              />
            ) : (
              <span
                className={`bg-muted flex size-20 items-center justify-center rounded-md border ${dnr ? "border-destructive ring-destructive ring-2 ring-offset-2" : ""}`}
              >
                <User className="text-muted-foreground size-9" aria-hidden="true" />
              </span>
            )}
            {dnr ? (
              <span className="bg-destructive text-destructive-foreground absolute -bottom-1.5 left-1/2 -translate-x-1/2 rounded-xs px-2 py-px text-xs font-semibold whitespace-nowrap">
                {codeStatusShortLabel(resident.codeStatus)}
              </span>
            ) : null}
          </span>
          <span className="min-w-0">
            <span className="block text-2xl font-semibold">
              {resident.firstName} {resident.lastName}
            </span>
            <span className="text-muted-foreground block text-sm">
              {resident.roomNumber ? `Room ${resident.roomNumber}, ` : ""}
              {facilityName || "Assisted living facility"}
            </span>
          </span>
        </div>

        <div className="flex flex-col gap-6">
          <FaceSheetBlock
            title="Admission information"
            onEdit={editResident}
            columns="md:grid-cols-2 lg:grid-cols-3"
          >
            <FieldRow label="Admit date" value={formatSheetDate(resident.admissionDate, zone)} />
            <FieldRow label="Time" value={resident.admissionTime} />
            <FieldRow label="Room" value={resident.roomNumber} />
          </FaceSheetBlock>
          <FaceSheetBlock title="Resident information" onEdit={editResident}>
            <FieldRow label="Last name" value={resident.lastName} />
            <FieldRow label="First name" value={resident.firstName} />
            <FieldRow label="Middle name" value={resident.middleName} />
            <FieldRow label="Preferred name" value={resident.preferredName} />
            <FieldRow label="Date of birth" value={formatSheetDate(resident.dateOfBirth, zone)} />
            <FieldRow label="Gender" value={resident.gender} />
          </FaceSheetBlock>
          <FaceSheetBlock title="Prior address" onEdit={editResident}>
            <FieldRow label="Address" value={resident.priorAddress} className="md:col-span-2" />
            <FieldRow label="City" value={resident.priorCity} />
            <FieldRow label="State" value={resident.priorState} />
            <FieldRow label="Zip code" value={resident.priorZipCode} />
          </FaceSheetBlock>
          <FaceSheetBlock title="Identification & insurance" onEdit={editResident}>
            <FieldRow
              label="Social Security"
              value={shown(hidden, "ssn", formatSsn(resident.ssn))}
            />
            <FieldRow
              label="Medicare"
              value={shown(hidden, "medicareNumber", resident.medicareNumber)}
            />
            <FieldRow label="Medicaid" value={shown(hidden, "medicaidId", resident.medicaidId)} />
            <FieldRow
              label="Medicaid managed by"
              value={shown(hidden, "medicaidManagedBy", resident.medicaidManagedBy)}
            />
            <FieldRow
              label="Other ins name"
              value={shown(hidden, "otherInsuranceName", resident.otherInsuranceName)}
            />
            <FieldRow
              label="Other ins #"
              value={shown(hidden, "otherInsuranceNumber", resident.otherInsuranceNumber)}
            />
          </FaceSheetBlock>

          <FaceSheetVitals
            residentId={residentId}
            zone={zone}
            onRecord={() => goTo("/residents/$residentId/vitals")}
          />

          <FaceSheetBlock
            title="Demographics"
            onEdit={editResident}
            columns="md:grid-cols-2 lg:grid-cols-4"
          >
            <FieldRow label="Religion" value={resident.religion} />
            <FieldRow label="Place of birth" value={resident.placeOfBirth} />
            <FieldRow label="Ethnicity" value={resident.ethnicity} />
            <FieldRow label="Language" value={resident.language} />
          </FaceSheetBlock>
          <FaceSheetBlock title="Family background" onEdit={editResident} columns="md:grid-cols-3">
            <FieldRow label="Father name" value={resident.fatherName} />
            <FieldRow label="Mother's maiden" value={resident.mothersMaidenName} />
            <FieldRow label="Pastor/clergy" value={resident.pastor} />
          </FaceSheetBlock>
          <FaceSheetBlock
            title="Code status & allergies"
            onEdit={() => goTo("/residents/$residentId")}
            editLabel="Edit on Profile"
          >
            <div className="rounded-md border p-3">
              <p className="text-muted-foreground text-xs font-medium">Code status</p>
              <p className={`mt-1.5 text-sm font-semibold ${dnr ? "text-destructive" : ""}`}>
                {codeStatusLabel(resident.codeStatus) ?? "Not recorded"}
              </p>
            </div>
            <div className="rounded-md border p-3">
              <p className="text-muted-foreground text-xs font-medium">Allergies</p>
              <p className="mt-1.5 text-sm">{allergySummaryLine(resident.allergies)}</p>
            </div>
          </FaceSheetBlock>
          <FaceSheetBlock title="History" onEdit={editResident}>
            <FieldRow label="Prior occupation" value={resident.priorOccupation} />
            <FieldRow label="Military service" value={resident.militaryService} />
            <FieldRow label="Admitted from" value={resident.admittedFrom} />
            <FieldRow label="Referred by" value={resident.referredBy} />
            <FieldRow
              label="Diagnosis"
              value={shown(hidden, "diagnosis", resident.diagnosis)}
              className="md:col-span-2"
            />
          </FaceSheetBlock>
          <div>
            <FaceSheetBlock title="Medical providers" onEdit={editResident}>
              <FieldRow label="Home health" value={resident.homeHealthProvider} />
              <FieldRow label="Hospice" value={resident.hospiceProvider} />
            </FaceSheetBlock>
            <Separator className="my-3" />
            <div className="grid grid-cols-1 gap-x-8 gap-y-1 px-1 md:grid-cols-2">
              <FieldRow label="Attending physician" value={provider} />
              <FieldRow label="Hospital" value={resident.preferredHospital} />
              <FieldRow label="Alt physician" value={resident.altPhysician} />
              <FieldRow label="Pharmacy" value={pharmacy} />
              <FieldRow label="Dentist" value={resident.dentist} />
              <FieldRow label="Funeral home" value={resident.funeralHome} />
            </div>
          </div>
          <div>
            <div className="flex items-center justify-between gap-2 border-b pb-1.5">
              <h2 className="text-sm font-semibold">Emergency contacts</h2>
              <Button variant="ghost" size="sm" asChild>
                <Link to="/residents/$residentId/family" params={{ residentId }}>
                  <Pencil className="size-3" />
                  Edit on Family
                </Link>
              </Button>
            </div>
            <FaceSheetContacts sheet={sheet.data} />
          </div>
          <div className="text-muted-foreground mt-8 border-t pt-4 text-center text-xs">
            Generated on{" "}
            {new Date(sheet.dataUpdatedAt).toLocaleString("en-US", {
              dateStyle: "long",
              timeStyle: "short",
            })}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
